import React, { useRef } from 'react';
import TableShape from './TableShape';
import LayoutElementShape from './LayoutElementShape';
import useFloorPlanFit from '../../hooks/useFloorPlanFit';

// The editor needs room to actually build a large floor plan (60+ tables), not just display one
// that's already finished - a canvas sized to whatever's already placed becomes a wall staff
// can't drag past. This is a starting floor, not a cap: useFloorPlanFit still grows it further via
// its own Math.max(...) once tables get placed near/beyond this size, so it's never a hard limit.
const EDITOR_MIN_WIDTH = 2400;
const EDITOR_MIN_HEIGHT = 1400;

const FloorPlanCanvas = ({
  tables,
  elements,
  selectedTableIds = [],
  selectedElementIds = [],
  onSelectTable,
  onSelectElement,
  onDeselect,
  onTableChange,
  onElementChange,
  readOnly = false,
  occupiedTables = {},
  multiSelectedIds = [],
  onCheckout,
  onPrintMains,
  onPrintDessert,
  onInfoClick,
  getElapsed,
  // Draft tables from "Import Floor Plan" (see FloorPlanEditorPage) - rendered separately from
  // `tables` rather than merged in, so a not-yet-confirmed import can never get swept up in the
  // real dirty-tracking/Save Layout flow. They still count toward the canvas's own bounding box
  // (below) so the editor grows to fit an imported layout the same way it grows for real tables.
  draftTables = [],
  selectedDraftTableId = null,
  onSelectDraftTable,
  onDraftTableChange,
}) => {
  const containerRef = useRef(null);
  // The rotate handle only makes sense for one shape at a time (it drives a single onRotate
  // angle) - hide it once a group of 2+ tables/elements is selected together, even though the
  // selection-highlight border itself renders on every selected shape.
  const isSoleSelection = selectedTableIds.length + selectedElementIds.length === 1;
  // Bounds are computed from the *saved* positions - both the editor and the read-only
  // order-view dashboard render at natural (unscaled) size, so placing new tables/elements in the
  // editor is never blocked by a shrink-to-fit boundary, and what's built there looks the same
  // size in daily use (real scroll, via .floorPlanCanvas's existing overflow:auto, reaches
  // whatever doesn't fit on screen either way).
  const { scale, contentWidth, contentHeight } = useFloorPlanFit([...tables, ...elements, ...draftTables], {
    minWidth: readOnly ? undefined : EDITOR_MIN_WIDTH,
    minHeight: readOnly ? undefined : EDITOR_MIN_HEIGHT,
  });

  return (
    <div className="floorPlanCanvas" ref={containerRef} onMouseDown={readOnly ? undefined : onDeselect}>
      {tables.length === 0 && elements.length === 0 && draftTables.length === 0 && (
        <p className="floorPlanEmptyState">Nothing in this zone yet — use Add Table or Add Element below.</p>
      )}
      {/*
        Two layers, not one: a CSS `transform: scale()` shrinks how an element PAINTS, but not the
        box it occupies for its ancestor's layout/scrolling purposes - a scaled-down inner layer
        alone would still make `.floorPlanCanvas` think the content is full (unscaled) size and
        scroll for room that, visually, is no longer needed. The outer layer is sized to the actual
        on-screen (scaled) footprint so the canvas's overflow/scroll math sees the real size; the
        inner layer carries the transform against the content's natural (unscaled) size, which is
        the coordinate space every table's saved position/size and react-rnd's own drag math
        already assume.
      */}
      <div
        className="floorPlanScaleLayerOuter"
        style={{ width: contentWidth * scale, height: contentHeight * scale }}
      >
        <div
          className="floorPlanScaleLayer"
          style={{ width: contentWidth, height: contentHeight, transform: `scale(${scale})` }}
        >
          {tables.map((table) => {
            const occupiedInfo = occupiedTables[table.id];
            return (
              <TableShape
                key={table.id}
                table={table}
                isSelected={selectedTableIds.includes(table.id)}
                isSoleSelection={isSoleSelection}
                onSelect={onSelectTable}
                onChange={onTableChange}
                readOnly={readOnly}
                isOccupied={!!occupiedInfo}
                elapsedLabel={occupiedInfo && getElapsed ? getElapsed(occupiedInfo.started) : ''}
                isMultiSelected={multiSelectedIds.includes(table.id)}
                onCheckout={() => onCheckout && onCheckout(table.id)}
                onPrintMains={() => onPrintMains && onPrintMains(table.id)}
                onPrintDessert={() => onPrintDessert && onPrintDessert(table.id)}
                onInfoClick={() => onInfoClick && onInfoClick(table.id, table.name)}
                canvasScale={scale}
              />
            );
          })}
          {elements.map((element) => (
            <LayoutElementShape
              key={element.id}
              element={element}
              isSelected={selectedElementIds.includes(element.id)}
              isSoleSelection={isSoleSelection}
              onSelect={onSelectElement}
              onChange={onElementChange}
              readOnly={readOnly}
              canvasScale={scale}
            />
          ))}
          {draftTables.map((table) => (
            <TableShape
              key={table.id}
              table={table}
              isSelected={selectedDraftTableId === table.id}
              isSoleSelection
              isDraft
              onSelect={onSelectDraftTable}
              onChange={onDraftTableChange}
              readOnly={false}
              canvasScale={scale}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FloorPlanCanvas;
